import type { ReactNode } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { AsyncStatus } from "@gui/widgets/A11y";
import { EmptyState, type EmptyStateProps } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { resolveAsyncState } from "./resolveAsyncState";

export interface AsyncStateProps {
	loading: boolean;
	/** The page's error message, if any. It takes over the surface only when there is no data. */
	error?: string | null;
	/** True when the load produced nothing to render as `children`. */
	isEmpty: boolean;
	/**
	 * Whether this surface's own load failed. Leave it out when `error` only ever means that;
	 * pass it when `error` can also carry a failure from elsewhere (another tab, an action).
	 */
	loadFailed?: boolean;
	/** Announced while loading. Name the thing being loaded, not just "Loading". */
	loadingText: string;
	/** Shown while loading, in place of the default spinner: skeletons in the page's own shape. */
	loadingFallback?: ReactNode;
	empty: EmptyStateProps;
	/** Heading of the failed-load state: "Couldn't load job errors". */
	errorTitle: string;
	onRetry?: () => void;
	children: ReactNode;
}

/** Centred the way the empty and error states are: in a flex column (a `fill` body, a dialog) it
 *  takes the free height, so the spinner sits where the content will. */
const DEFAULT_LOADING_FALLBACK = (
	<Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
		<CircularProgress />
	</Box>
);

/**
 * Renders exactly one of loading / error / empty / ready for a data-backed surface, so a
 * failed load can never sit beside a "nothing found" message.
 *
 * Put `aria-busy={loading}` on the region that owns the content; this widget carries the text
 * equivalent of each state itself.
 */
export function AsyncState(props: AsyncStateProps) {
	// An error that is not this surface's failed load is reported above whatever is shown.
	const errorBanner = props.error && (
		// Capped, and scrolls itself: in a `fill` page the body does not scroll, so a long banner
		// (a batch run's every failure) would otherwise squeeze the content under it to nothing.
		<Alert severity="error" sx={{ mb: 2, flex: "none", maxHeight: "30vh", overflowY: "auto" }}>
			{props.error}
		</Alert>
	);

	switch (resolveAsyncState(props)) {
		case "loading":
			return (
				<>
					<AsyncStatus state="loading" loadingText={props.loadingText} />
					{props.loadingFallback ?? DEFAULT_LOADING_FALLBACK}
				</>
			);
		case "error":
			return <ErrorState title={props.errorTitle} detail={props.error} onRetry={props.onRetry} />;
		case "empty":
			return (
				<>
					{errorBanner}
					<EmptyState {...props.empty} />
				</>
			);
		case "ready":
			return (
				<>
					{errorBanner}
					{props.children}
				</>
			);
	}
}
